import { useState } from "react";
import { Input } from "@/components/ui/input";
import CropModal from "@/components/editorial/CropModal";
import { ImageSlot, MediaLibraryPicker } from "@/components/editorial/EditorialMediaControls";
import {
  structureHeroSource,
  heroUsesPrimaryImageOnMobile,
  buildHeroSourceExtra,
  deriveDetectedFieldsFromContent,
} from "@/lib/editorialHeroAuthoring.mjs";
import { editorialMediaFrameAspect, editorialMediaFrameLabel } from "@/lib/editorialMediaFrame.mjs";
import { HERO_BACKGROUNDS, HERO_PALETTES, HERO_VARIANTS } from "./EditorialHeroMaster";

function VariantThumb({ variant }) {
  const photo = "bg-espresso/20 rounded";
  const line = "bg-espresso/40 rounded h-1";
  if (variant === "A") return <div className="grid grid-cols-[3fr_2fr] gap-1.5 h-14"><div className={photo} /><div className="flex flex-col justify-center gap-1"><div className={`${line} w-3/4`} /><div className={`${line} w-full`} /><div className={`${line} w-2/3`} /></div></div>;
  if (variant === "B") return <div className="grid grid-cols-[2fr_3fr] gap-1.5 h-14"><div className="flex flex-col justify-center gap-1"><div className={`${line} w-3/4`} /><div className={`${line} w-full`} /><div className={`${line} w-2/3`} /></div><div className={photo} /></div>;
  return <div className="relative h-14"><div className={`${photo} absolute inset-0`} /><div className="absolute inset-x-2 bottom-2 space-y-1"><div className="h-1 w-3/4 rounded bg-white/80" /><div className="h-1 w-1/2 rounded bg-white/80" /></div></div>;
}

export function HeroAuthoringEditor({ block, onPatch, onContinueInNextSpread }) {
  const content = block.content || {};
  const settings = block.settings || {};
  const variant = settings.variant || "A";
  const [sourceText, setSourceText] = useState(content.extra?.hero_source_text || "");
  const [showStructured, setShowStructured] = useState(Boolean(content.headline || content.eyebrow || content.subtitle || content.lead));
  const [showDesign, setShowDesign] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [showEyebrowField, setShowEyebrowField] = useState(Boolean(content.eyebrow));
  const [showLeadField, setShowLeadField] = useState(Boolean(content.lead));
  const [libraryFor, setLibraryFor] = useState(null);
  const [cropFor, setCropFor] = useState(null);
  const [showMobileOverride, setShowMobileOverride] = useState(!heroUsesPrimaryImageOnMobile(content));
  const [parseFeedback, setParseFeedback] = useState(() => {
    const source = content.extra?.hero_source_text || "";
    if (!source.trim()) return null;
    const leftoverText = content.extra?.hero_leftover_text || "";
    const detectedFields = deriveDetectedFieldsFromContent(content);
    return { detectedFields, leftoverText };
  });

  const patchContent = (patch) => onPatch({ content: { ...content, ...patch } });
  const patchSettings = (patch) => onPatch({ settings: { ...settings, ...patch } });

  const desktopAspect = editorialMediaFrameAspect("HERO", { mode: "desktop", variant });
  const mobileAspect = editorialMediaFrameAspect("HERO", { mode: "mobile", variant });
  const mobileUsesPrimary = heroUsesPrimaryImageOnMobile(content);

  const structure = () => {
    const recognized = structureHeroSource(sourceText);
    const { eyebrow, headline, subtitle, lead, leftoverText, detectedFields } = recognized;

    patchContent({
      eyebrow,
      headline,
      subtitle,
      lead,
      extra: {
        ...(content.extra || {}),
        ...buildHeroSourceExtra(sourceText, recognized),
      },
    });

    setParseFeedback({ detectedFields, leftoverText });
    setShowEyebrowField(Boolean(eyebrow));
    setShowLeadField(Boolean(lead));
    setShowStructured(true);
  };

  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    if (cropFor === "mobile") {
      patchSettings({ mobile_focal_x: focal_x, mobile_focal_y: focal_y, hero_mobile_zoom: zoom });
    } else {
      patchSettings({ focal_x, focal_y, hero_zoom: zoom });
    }
    setCropFor(null);
  };

  const acceptMedia = (media, slot) => {
    if (slot === "mobile") {
      patchContent({ hero_mobile_media_id: media.id, hero_mobile_url: media.file_url || media.url });
    } else {
      patchContent({ hero_media_id: media.id, hero_url: media.file_url || media.url });
    }
    setLibraryFor(null);
  };

  return (
    <div className="space-y-4" data-testid="hero-authoring-clean">
      <section className="rounded-2xl border hairline bg-white p-4 md:p-5 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/55">01 · Вход</div>
          <h3 className="mt-1 font-serif text-xl text-espresso">Вставьте текст и добавьте одно фото</h3>
          <p className="mt-1 text-sm text-espresso/65">
            Обложка предназначена для названия, короткого подзаголовка и небольшого лида.
            Если вставить большой текст, Редакция оставит на обложке только необходимое,
            а остальной текст сохранит для следующего разворота.
          </p>
        </div>

        <div>
          <label className="mb-1 block text-[11px] text-espresso/65">Исходный текст</label>
          <textarea value={sourceText} onChange={(e) => setSourceText(e.target.value)} rows={7}
            placeholder={"БИСКВИТЫ · 01\nPain de Gênes\nВлажный миндальный бисквит\nКлассический французский бисквит..."}
            className="w-full rounded-xl border border-espresso/20 bg-white px-3 py-3 text-sm leading-relaxed outline-none focus:border-espresso/45"
            data-testid="hero-source-text" />
        </div>

        <ImageSlot
          label="Основное фото"
          url={content.hero_url}
          focalX={settings.focal_x ?? 0.5}
          focalY={settings.focal_y ?? 0.5}
          zoom={settings.hero_zoom || 1}
          frameAspect={desktopAspect}
          frameLabel={editorialMediaFrameLabel("HERO", { mode: "desktop", variant })}
          onFile={(media) => patchContent({ hero_media_id: media.id, hero_url: media.file_url || media.url })}
          onClear={() => patchContent({ hero_media_id: null, hero_url: null })}
          onOpenLibrary={() => setLibraryFor("desktop")}
          onOpenCrop={() => setCropFor("desktop")}
          testid="hero-primary-image"
        />

        <div className="rounded-xl border border-espresso/10 bg-cream/45 px-3 py-3 text-sm text-espresso/70">
          <div className="font-medium text-espresso">Mobile</div>
          <div className="mt-0.5">{mobileUsesPrimary ? "Используется основное фото" : "Используется отдельное фото для телефона"}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => setCropFor("mobile")} disabled={!content.hero_url && mobileUsesPrimary}
              className="rounded-lg border border-espresso/20 bg-white px-3 py-1.5 text-xs disabled:opacity-40">
              Настроить кадр для телефона
            </button>
            {!showMobileOverride && (
              <button type="button" onClick={() => setShowMobileOverride(true)} className="px-2 py-1.5 text-xs text-espresso/60">
                Использовать другое фото
              </button>
            )}
          </div>
        </div>

        {showMobileOverride && (
          <div className="rounded-xl border border-dashed border-espresso/20 p-3">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-espresso">Отдельное фото для телефона · необязательно</div>
                <div className="text-xs text-espresso/60">Оставьте пустым, чтобы Mobile автоматически использовал основное фото.</div>
              </div>
              {!mobileUsesPrimary && (
                <button type="button" onClick={() => {
                  patchContent({ hero_mobile_media_id: null, hero_mobile_url: null });
                  setShowMobileOverride(false);
                }} className="text-xs text-espresso/60">Вернуть основное фото</button>
              )}
            </div>
            <ImageSlot
              label="Фото для телефона"
              url={content.hero_mobile_url}
              focalX={settings.mobile_focal_x ?? settings.focal_x ?? 0.5}
              focalY={settings.mobile_focal_y ?? settings.focal_y ?? 0.5}
              zoom={settings.hero_mobile_zoom || settings.hero_zoom || 1}
              frameAspect={mobileAspect}
              frameLabel={editorialMediaFrameLabel("HERO", { mode: "mobile", variant })}
              onFile={(media) => patchContent({ hero_mobile_media_id: media.id, hero_mobile_url: media.file_url || media.url })}
              onClear={() => patchContent({ hero_mobile_media_id: null, hero_mobile_url: null })}
              onOpenLibrary={() => setLibraryFor("mobile")}
              onOpenCrop={() => setCropFor("mobile")}
              testid="hero-mobile-override"
            />
          </div>
        )}

        <button type="button" onClick={structure} disabled={!sourceText.trim()}
          className="rounded-xl bg-espresso px-5 py-2.5 text-sm text-cream disabled:opacity-40"
          data-testid="hero-structure-source">
          Разобрать текст
        </button>

        {parseFeedback && (
          <div className="rounded-xl border border-espresso/15 bg-cream/60 px-4 py-3 text-sm" data-testid="hero-parse-feedback">
            <div className="flex items-center gap-2 font-medium text-espresso">
              <span aria-hidden="true">✓</span>
              <span>Текст разобран</span>
            </div>
            {parseFeedback.detectedFields.length > 0 ? (
              <div className="mt-1 text-espresso/70">
                Найдены: {parseFeedback.detectedFields.join(", ")}. Проверьте результат.
              </div>
            ) : (
              <div className="mt-1 text-espresso/70">
                Структура требует проверки. Заполните нужные поля вручную.
              </div>
            )}

            {parseFeedback.leftoverText && (
              <div className="mt-3 border-t border-espresso/10 pt-3">
                <div className="text-espresso/70">Остался текст, который не используется в обложке.</div>
                {onContinueInNextSpread && (
                  <button
                    type="button"
                    onClick={() => onContinueInNextSpread(parseFeedback.leftoverText)}
                    className="mt-2 rounded-lg border border-espresso/25 bg-white px-3 py-1.5 text-xs text-espresso"
                    data-testid="hero-continue-next-spread"
                  >
                    Продолжить в следующем развороте
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {showStructured && (
        <section className="rounded-2xl border hairline bg-white p-4 md:p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/55">02 · Проверьте результат</div>
              <h3 className="mt-1 font-serif text-xl text-espresso">Проверьте структуру</h3>
              <p className="mt-1 text-sm text-espresso/65">Исправляйте только то, что действительно нужно.</p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {(showEyebrowField || content.eyebrow) ? (
              <div>
                <label className="mb-1 block text-[11px] text-espresso/60">Надзаголовок</label>
                <Input value={content.eyebrow || ""} onChange={(e) => patchContent({ eyebrow: e.target.value })} />
              </div>
            ) : (
              <div className="flex items-end">
                <button type="button" onClick={() => setShowEyebrowField(true)} className="text-xs text-espresso/60 underline underline-offset-2">
                  + Добавить надзаголовок
                </button>
              </div>
            )}

            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Заголовок</label>
              <Input value={content.headline || ""} onChange={(e) => patchContent({ headline: e.target.value })} />
            </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-[11px] text-espresso/60">Подзаголовок</label>
              <Input value={content.subtitle || ""} onChange={(e) => patchContent({ subtitle: e.target.value })} />
            </div>

            {(showLeadField || content.lead) ? (
              <div className="md:col-span-2">
                <label className="mb-1 block text-[11px] text-espresso/60">Короткий лид</label>
                <textarea value={content.lead || ""} onChange={(e) => patchContent({ lead: e.target.value })} rows={4} className="w-full rounded-lg border px-3 py-2 text-sm" />
              </div>
            ) : (
              <div className="md:col-span-2">
                <button type="button" onClick={() => setShowLeadField(true)} className="text-xs text-espresso/60 underline underline-offset-2">
                  + Добавить лид
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-espresso/10 pt-3">
            <button type="button" onClick={() => setShowOptionalFields((v) => !v)} className="flex w-full items-center justify-between text-left">
              <span className="text-sm font-medium text-espresso">Дополнительные поля</span>
              <span className="text-sm text-espresso/60">{showOptionalFields ? "Скрыть" : "Открыть"}</span>
            </button>
            {showOptionalFields && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-1 block text-[11px] text-espresso/60">Подпись к фото</label>
                  <Input value={content.caption || ""} onChange={(e) => patchContent({ caption: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-[11px] text-espresso/60">Описание изображения</label>
                  <Input value={content.alt || ""} onChange={(e) => patchContent({ alt: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="rounded-2xl border hairline bg-white p-4 md:p-5">
        <button type="button" onClick={() => setShowDesign((v) => !v)} className="flex w-full items-center justify-between text-left">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/55">03 · Оформление</div>
            <div className="mt-1 font-serif text-xl text-espresso">Композиция обложки</div>
          </div>
          <span className="text-sm text-espresso/60">{showDesign ? "Скрыть" : "Открыть"}</span>
        </button>

        {showDesign && (
          <div className="mt-5 space-y-5">
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-widest opacity-70">Композиция</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {HERO_VARIANTS.map((item) => (
                  <button key={item.key} type="button" onClick={() => patchSettings({ variant: item.key })}
                    className={`rounded-xl border p-3 text-left transition ${variant === item.key ? "border-espresso bg-espresso/5" : "border-espresso/15 hover:bg-cream/60"}`}>
                    <VariantThumb variant={item.key} />
                    <div className="mt-2 text-[10px] uppercase tracking-widest opacity-70">Вариант {item.key}</div>
                    <div className="mt-1 text-[11px] leading-snug">{item.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-[10px] uppercase tracking-widest opacity-70">Акцентный цвет</div>
              <div className="flex flex-wrap gap-2">
                {HERO_PALETTES.map((item) => <button key={item.key} type="button" onClick={() => patchSettings({ palette: item.key })}
                  className={`h-8 w-8 rounded-full border-2 ${settings.palette === item.key ? "border-espresso scale-110" : "border-white shadow"}`} title={item.label} style={{ background: item.swatch }} />)}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-widest opacity-70">Фон</label>
              <select value={settings.background || "warm-ivory"} onChange={(e) => patchSettings({ background: e.target.value })}
                className="h-10 w-full rounded-md border border-espresso/25 bg-white px-3 text-sm outline-none focus:border-espresso">
                {HERO_BACKGROUNDS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3"><input type="checkbox" checked={!!settings.botanical} onChange={(e) => patchSettings({ botanical: e.target.checked })} /><span className="text-sm">Ботаническая декоративная деталь</span></label>
          </div>
        )}
      </section>

      <MediaLibraryPicker open={!!libraryFor} onClose={() => setLibraryFor(null)} onPick={(media) => acceptMedia(media, libraryFor)} />
      <CropModal open={!!cropFor} mode={cropFor || "desktop"}
        url={cropFor === "mobile" ? (content.hero_mobile_url || content.hero_url) : content.hero_url}
        aspectRatio={cropFor === "mobile" ? mobileAspect : desktopAspect}
        frameLabel={editorialMediaFrameLabel("HERO", { mode: cropFor === "mobile" ? "mobile" : "desktop", variant })}
        focalX={cropFor === "mobile" ? (settings.mobile_focal_x ?? settings.focal_x ?? 0.5) : (settings.focal_x ?? 0.5)}
        focalY={cropFor === "mobile" ? (settings.mobile_focal_y ?? settings.focal_y ?? 0.5) : (settings.focal_y ?? 0.5)}
        zoom={cropFor === "mobile" ? (settings.hero_mobile_zoom || settings.hero_zoom || 1) : (settings.hero_zoom || 1)}
        onSave={saveCrop} onClose={() => setCropFor(null)} />
    </div>
  );
}
